import { useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { Button } from "components/Core";
import { apiPath } from "constants/index";
import { useLocal } from "hooks/useLocal";
import styles from "./publicRequestPage.module.scss";
import { CheckIcon } from "assets/icons";

const PublicRequestPage = ({
  endpoint,
  title,
  subtitle,
  ctaLabel,
  successTitle,
  successDescription,
  includeNote = false,
}) => {
  const { isArabic, direction } = useLocal();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const copy = {
    emailLabel: isArabic ? "البريد الإلكتروني" : "Email",
    emailPlaceholder: isArabic ? "you@example.com" : "you@example.com",
    phoneLabel: isArabic ? "رقم الهاتف" : "Phone number",
    phonePlaceholder: isArabic ? "+973..." : "+973...",
    noteLabel: isArabic ? "كيف يمكننا مساعدتك؟" : "How can we help?",
    notePlaceholder: isArabic
      ? "اكتب طلب الدعم الخاص بك"
      : "Write your support request",
    successBadge: isArabic ? "تم بنجاح" : "Success",
    phoneRequired: isArabic
      ? "يرجى إدخال رقم هاتف صحيح"
      : "Please enter a valid phone number",
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const formData = new FormData(event.currentTarget);
    const normalizedPhone = phoneNumber.trim();
    if (!normalizedPhone || normalizedPhone.length < 6) {
      setError(copy.phoneRequired);
      return;
    }

    const payload = {
      email: String(formData.get("email") || "")
        .trim()
        .toLowerCase(),
      phone_number: `+${normalizedPhone}`,
    };

    if (includeNote) {
      payload.note = String(formData.get("note") || "").trim();
    }

    setLoading(true);
    try {
      const response = await fetch(`${apiPath}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || "Unable to submit your request now.");
      }

      setSuccess(true);
      setPhoneNumber("");
      event.currentTarget.reset();
    } catch (requestError) {
      setError(requestError?.message || "Unable to submit your request now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.wrapper}>
      <div className={styles.card} dir={direction}>
        {!success ? (
          <>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>

            {error && (
              <div className={styles.alertWrap}>
                <Alert severity="error">{error}</Alert>
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <label htmlFor="email">{copy.emailLabel}</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                disabled={loading}
                placeholder={copy.emailPlaceholder}
                style={{
                  direction: "ltr",
                }}
              />

              <label htmlFor="phone_number">{copy.phoneLabel}</label>
              <div className={styles.phoneInputWrap}>
                <PhoneInput
                  country={isArabic ? "sa" : "bh"}
                  enableSearch
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                  disabled={loading}
                  containerStyle={{ direction: "ltr" }}
                  inputProps={{
                    name: "phone_number",
                    required: true,
                    id: "phone_number",
                  }}
                  inputClass={styles.phoneInputField}
                  buttonClass={styles.phoneInputButton}
                  dropdownClass={styles.phoneInputDropdown}
                  searchClass={styles.phoneInputSearch}
                />
              </div>

              {includeNote && (
                <>
                  <label htmlFor="note">{copy.noteLabel}</label>
                  <textarea
                    id="note"
                    name="note"
                    required
                    disabled={loading}
                    placeholder={copy.notePlaceholder}
                    rows={5}
                  />
                </>
              )}

              <div className={`${styles.ctaWrap} `}>
                <Button
                  variant="smallCTA"
                  type="submit"
                  disabled={loading}
                  minWidth={170}
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    ctaLabel
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className={styles.successWrap}>
            <CheckIcon />
            {/* <p className={styles.successBadge}>{copy.successBadge}</p> */}
            <h2 className={styles.successTitle}>{successTitle}</h2>
            <p className={styles.successText}>{successDescription}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicRequestPage;
